import { useApiQuery } from '../useApiQuery';
import { invoiceKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api/client';
import { toInvoiceStatusApiParam } from '@/lib/invoice-status';
import type { Invoice, InvoiceListParams, InvoiceListResponse } from '@/types/invoice';
import { normalizeInvoice } from './useInvoice';
import { hasClearingMoneyInFlight } from '@/lib/invoices/shown-invoice-money';

interface OperationsListResponse {
  data: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  has_more: boolean;
}

interface UseInvoicesOptions {
  enabled?: boolean;
  refetchOnMount?: boolean | 'always';
  staleTime?: number;
}

interface UseJobInvoicesOptions extends UseInvoicesOptions {
  /**
   * True while the caller knows an invoice is on its way — one the server
   * raises a few seconds after something it did — so the list is read again
   * until that invoice is in it.
   */
  pollWhile?: (invoices: Invoice[]) => boolean;
}

/** The most invoices the server lists in one page. */
const JOB_INVOICES_PAGE_SIZE = 100;

/** How often a job's invoices are read again while one is on its way. */
const AWAITED_INVOICE_POLL_MS = 5_000;

function toInvoiceListApiParams(params: InvoiceListParams) {
  const { status, ...rest } = params;
  return { ...rest, status: toInvoiceStatusApiParam(status) };
}

export function useInvoices(params: InvoiceListParams = {}, options: UseInvoicesOptions = {}) {
  return useApiQuery<InvoiceListResponse>({
    queryKey: invoiceKeys.list(params),
    queryFn: async () => {
      const raw = await apiGet<OperationsListResponse | InvoiceListResponse>(
        operationsClient,
        invoiceEndpoints.list(),
        { params: toInvoiceListApiParams(params) },
      );

      if ('data' in raw && Array.isArray((raw as OperationsListResponse).data)) {
        const ops = raw as OperationsListResponse;
        return {
          invoices: ops.data.map((inv) => normalizeInvoice(inv)) as Invoice[],
          total: ops.total,
          page: ops.page,
          limit: ops.limit,
          totalPages: ops.totalPages,
        };
      }

      const response = raw as InvoiceListResponse;
      return {
        ...response,
        invoices: response.invoices.map((inv) =>
          normalizeInvoice(inv as unknown as Record<string, unknown>),
        ),
      };
    },
    enabled: options.enabled,
    refetchInterval: (query) =>
      query.state.data?.invoices.some(hasClearingMoneyInFlight) ? 30_000 : false,
    showErrorToast: false,
  });
}

async function fetchJobInvoicesPage(
  jobId: string,
  params: InvoiceListParams,
): Promise<InvoiceListResponse> {
  const raw = await apiGet<OperationsListResponse | InvoiceListResponse>(
    operationsClient,
    invoiceEndpoints.listByJob(jobId),
    { params: toInvoiceListApiParams(params) },
  );

  if ('data' in raw && Array.isArray((raw as OperationsListResponse).data)) {
    const ops = raw as OperationsListResponse;
    return {
      invoices: ops.data.map((inv) => normalizeInvoice(inv)) as Invoice[],
      total: ops.total,
      page: ops.page,
      limit: ops.limit,
      totalPages: ops.totalPages,
    };
  }

  const response = raw as InvoiceListResponse;
  return {
    ...response,
    invoices: response.invoices.map((inv) =>
      normalizeInvoice(inv as unknown as Record<string, unknown>),
    ),
  };
}

export function useJobInvoices(
  jobId: string,
  params: InvoiceListParams = {},
  options: UseJobInvoicesOptions = {},
) {
  return useApiQuery<InvoiceListResponse>({
    queryKey: invoiceKeys.byJob(jobId, params),
    queryFn: () => fetchJobInvoicesPage(jobId, params),
    enabled: Boolean(jobId) && (options.enabled ?? true),
    staleTime: options.staleTime,
    refetchOnMount: options.refetchOnMount,
    refetchInterval: (query) => {
      const invoices = query.state.data?.invoices ?? [];
      if (options.pollWhile?.(invoices)) return AWAITED_INVOICE_POLL_MS;
      return invoices.some(hasClearingMoneyInFlight) ? 30_000 : false;
    },
    showErrorToast: false,
  });
}

/**
 * Every invoice of a job, read page by page. A recurring series' page finds
 * the invoice that billed each visit here: read as the newest 100 only, a long
 * series showed its oldest visits as not billed, and billing one again was
 * refused because it already had an invoice.
 */
export function useAllJobInvoices(jobId: string, options: UseJobInvoicesOptions = {}) {
  return useApiQuery<InvoiceListResponse>({
    queryKey: invoiceKeys.byJob(jobId, { limit: JOB_INVOICES_PAGE_SIZE, pages: 'all' }),
    queryFn: async () => {
      const first = await fetchJobInvoicesPage(jobId, {
        page: 1,
        limit: JOB_INVOICES_PAGE_SIZE,
      });
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, (first.totalPages ?? 1) - 1) }, (_, index) =>
          fetchJobInvoicesPage(jobId, { page: index + 2, limit: JOB_INVOICES_PAGE_SIZE }),
        ),
      );
      const invoices = [first, ...rest].flatMap((page) => page.invoices);
      return {
        ...first,
        invoices,
        page: 1,
        limit: invoices.length,
        totalPages: 1,
      };
    },
    enabled: Boolean(jobId) && (options.enabled ?? true),
    staleTime: options.staleTime,
    refetchOnMount: options.refetchOnMount,
    refetchInterval: (query) => {
      const invoices = query.state.data?.invoices ?? [];
      if (options.pollWhile?.(invoices)) return AWAITED_INVOICE_POLL_MS;
      return invoices.some(hasClearingMoneyInFlight) ? 30_000 : false;
    },
    showErrorToast: false,
  });
}
